import { useMemo, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { Button, Dropdown, IconButton, Modal, type DropdownItem } from '@cyphex/ui';
import { formatSchedule } from '../../lib/time';

interface ScheduleMenuProps {
  onSchedule: (isoDateTime: string) => void;
  disabled?: boolean;
}

function atHour(daysAhead: number, hour: number): Date {
  const date = new Date();
  date.setDate(date.getDate() + daysAhead);
  date.setHours(hour, 0, 0, 0);
  return date;
}

function nextMonday(hour: number): Date {
  const date = new Date();
  const delta = (8 - date.getDay()) % 7 || 7;
  date.setDate(date.getDate() + delta);
  date.setHours(hour, 0, 0, 0);
  return date;
}

/** Send-later presets plus a custom picker. Mirrors `/conversations/:id/scheduled`. */
export function ScheduleMenu({ onSchedule, disabled }: ScheduleMenuProps) {
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState('');

  const items = useMemo<DropdownItem[]>(() => {
    const presets: Array<{ id: string; label: string; date: Date }> = [
      { id: 'soon', label: 'In 30 minutes', date: new Date(Date.now() + 30 * 60_000) },
      { id: 'tonight', label: 'Later today', date: atHour(0, 18) },
      { id: 'tomorrow', label: 'Tomorrow morning', date: atHour(1, 9) },
      { id: 'monday', label: 'Monday morning', date: nextMonday(9) },
    ];
    return [
      ...presets.map<DropdownItem>((preset) => ({
        id: preset.id,
        label: preset.label,
        shortcut: formatSchedule(preset.date.toISOString()),
        onSelect: () => onSchedule(preset.date.toISOString()),
      })),
      {
        id: 'custom',
        label: 'Pick a date and time…',
        separatorBefore: true,
        onSelect: () => setCustomOpen(true),
      },
    ];
  }, [onSchedule]);

  return (
    <>
      <Dropdown
        items={items}
        align="end"
        side="top"
        label="Send later"
        trigger={
          <IconButton
            icon={CalendarClock}
            label="Schedule send"
            size="xl"
            variant="ghost"
            disabled={disabled}
          />
        }
      />

      <Modal
        open={customOpen}
        onOpenChange={setCustomOpen}
        title="Schedule message"
        description="Choose when this message should be delivered."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCustomOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!custom}
              onClick={() => {
                onSchedule(new Date(custom).toISOString());
                setCustomOpen(false);
              }}
            >
              Schedule
            </Button>
          </div>
        }
      >
        <label className="block text-base font-medium text-secondary-foreground" htmlFor="schedule-at">
          Delivery time
        </label>
        <input
          id="schedule-at"
          type="datetime-local"
          value={custom}
          onChange={(event) => setCustom(event.target.value)}
          className="mt-1.5 w-full rounded-lg border border-input bg-card px-3 py-2 text-md text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/25"
        />
      </Modal>
    </>
  );
}
